import React, { useEffect } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { usePage } from '@inertiajs/react';

// Extend Window interface to include google property
declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement | null, options: any) => void;
          prompt: () => void;
          cancel: () => void;
        };
      };
    };
  }
}

const StyledButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 40,
  width: '100%',  // Ensure container takes full width
  '& > div': {  // Style for Google button container
    minWidth: 'fit-content !important',  // Allow button to take its natural width
    maxWidth: '100% !important',  // Maximum width for larger screens
    margin: '0 auto',  // Center the button
    [theme.breakpoints.down('sm')]: {
      width: '90% !important',  // Full width on mobile
    }
  }
}));

interface GoogleSignInButtonProps {
  googleCallback?: (response: any) => void;
  clientId?: string;
}

interface PageProps {
  config?: {
    google_client_id_oauth?: string;
    google_places_api_key?: string;
  };
  [key: string]: any;
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  googleCallback,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { props } = usePage<PageProps>();

  useEffect(() => {
    const scriptId = 'google-identity-script';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.id = scriptId;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    const handleCredentialResponse = (response: any) => {
      if(response.credential){
        googleCallback?.(response)
      }
    };

    const onScriptLoad = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: props.config?.google_client_id_oauth || "665150703117-5klv8plj5tomkj44i25hdknt0d2phpv7.apps.googleusercontent.com", // Fallback to hardcoded value if not in config
          callback: handleCredentialResponse,
          itp_support: true, // Enables special handling for Safari's 's ITP (Intelligent Tracking Prevention)
          use_fedcm_for_prompt: true,
        });

        window.google.accounts.id.renderButton(
          document.getElementById('google-signin-button'),
          {
            theme: 'filled_black',
            size: 'large',
            width: isMobile ? '100%' : 500, // Use percentage width on mobile
            shape: 'pill',
            text: 'continue_with',
            logo_alignment: 'left'
          }
        );
      }
    };

    if (script?.getAttribute('data-loaded')) {
      onScriptLoad();
    } else {
      script?.addEventListener('load', () => {
        script?.setAttribute('data-loaded', 'true');
        onScriptLoad();
      });
    }

    return () => {
      window.google?.accounts?.id?.cancel();
    };
  }, [theme.palette.mode, isMobile, googleCallback, props.config?.google_client_id_oauth]);

  return (
    <StyledButtonContainer>
      <div id="google-signin-button" />
    </StyledButtonContainer>
  );
};

export default GoogleSignInButton;
